import { type ChangeEvent, FC, useRef, useState } from "react";
import { CameraIcon, UploadIcon, XIcon } from "lucide-react";

import useApi from "@/hooks/useApi";
import useCamera from "@/hooks/useCamera";
import useDialog from "@/hooks/useDialog";
import { showErrorAlert, showSuccessAlert } from "@/Utils";
import { ApiResponse, getErrorMessage } from "@/types/IApi";
import { IEmployeeFace } from "@/types/IEmployeeFace";

import S from "./styles.module.scss";

interface FacialSelfEnrollDialogProps {
  onClose: () => void;
  onEnrolled: () => void;
}

type Step =
  | { kind: "choose" }
  | { kind: "camera" }
  | { kind: "preview"; file: File; url: string }
  | { kind: "saving" };

const FALLBACK_ERROR =
  "Não foi possível cadastrar o seu rosto agora. Tente novamente ou procure o RH.";

const FacialSelfEnrollDialog: FC<FacialSelfEnrollDialogProps> = ({
  onClose,
  onEnrolled,
}) => {
  const { postRequest } = useApi();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>({ kind: "choose" });

  const dialogRef = useDialog<HTMLDivElement>(onClose, true);

  const {
    videoRef,
    ready: cameraReady,
    error: cameraError,
    capture,
  } = useCamera(step.kind === "camera");

  const setPreview = (file: File) =>
    setStep({ kind: "preview", file, url: URL.createObjectURL(file) });

  const handleCaptureShot = async () => {
    const file = await capture();
    if (!file) {
      showErrorAlert("Não foi possível capturar a imagem da câmera.");
      return;
    }
    setPreview(file);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showErrorAlert("Envie uma imagem válida (PNG, JPG, GIF ou WEBP).");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setPreview(file);
  };

  const handleRetry = () => {
    if (step.kind === "preview") URL.revokeObjectURL(step.url);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setStep({ kind: "choose" });
  };

  const handleConfirm = async () => {
    if (step.kind !== "preview") return;
    const { file, url } = step;

    setStep({ kind: "saving" });

    try {
      const form = new FormData();
      form.append("image", file);
      const response = await postRequest("/api/employee-face/me", form);
      const body = (await response.json()) as ApiResponse<IEmployeeFace>;

      if (!body.response) throw new Error(body.message || FALLBACK_ERROR);

      showSuccessAlert(
        "Rosto cadastrado! A partir de agora você já bate o ponto pela facial."
      );
      URL.revokeObjectURL(url);
      onEnrolled();
      onClose();
    } catch (error: unknown) {
      showErrorAlert(getErrorMessage(error, FALLBACK_ERROR));
      setStep({ kind: "preview", file, url });
    }
  };

  return (
    <div className={S.overlay} onClick={onClose}>
      <div
        className={S.modal}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="facial-self-enroll-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={S.header}>
          <h2 id="facial-self-enroll-title">Cadastrar reconhecimento facial</h2>
          {step.kind !== "saving" && (
            <button
              type="button"
              className={S.closeBtn}
              title="Fechar"
              aria-label="Fechar"
              onClick={onClose}
            >
              <XIcon size={16} aria-hidden="true" />
            </button>
          )}
        </div>

        <p className={S.hint}>
          Enquadre o rosto de frente, com boa luz e sem óculos escuros ou boné.
          A foto é usada só para reconhecer você no ponto e não fica guardada.
        </p>

        {step.kind === "choose" && (
          <div className={S.choices}>
            <button
              type="button"
              className={S.choiceBtn}
              onClick={() => setStep({ kind: "camera" })}
            >
              <CameraIcon size={22} aria-hidden="true" />
              Tirar foto agora
            </button>
            <label className={S.choiceBtn} htmlFor="facial-self-enroll-file">
              <UploadIcon size={22} aria-hidden="true" />
              Escolher um arquivo
            </label>
            <input
              id="facial-self-enroll-file"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className={S.fileInput}
              aria-label="Escolher um arquivo"
              onChange={handleFileChange}
            />
          </div>
        )}

        {step.kind === "camera" && (
          <div className={S.camera}>
            <video
              ref={videoRef}
              className={S.video}
              autoPlay
              playsInline
              muted
            />
            {cameraError && (
              <p role="alert" className={S.error}>
                {cameraError}
              </p>
            )}
            <div className={S.actions}>
              <button
                type="button"
                className={S.cancelBtn}
                onClick={() => setStep({ kind: "choose" })}
              >
                Voltar
              </button>
              <button
                type="button"
                className={S.saveBtn}
                disabled={!cameraReady}
                onClick={() => void handleCaptureShot()}
              >
                Capturar
              </button>
            </div>
          </div>
        )}

        {step.kind === "preview" && (
          <div className={S.preview}>
            <img
              src={step.url}
              alt="Prévia da foto"
              className={S.previewImage}
            />
            <div className={S.actions}>
              <button
                type="button"
                className={S.cancelBtn}
                onClick={handleRetry}
              >
                Tentar novamente
              </button>
              <button
                type="button"
                className={S.saveBtn}
                onClick={() => void handleConfirm()}
              >
                Usar esta foto
              </button>
            </div>
          </div>
        )}

        {step.kind === "saving" && (
          <p className={S.state} role="status">
            Cadastrando…
          </p>
        )}
      </div>
    </div>
  );
};

export default FacialSelfEnrollDialog;
